/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';

import {Filter} from '../interfaces/filter';
import {Page} from './page';
import {StateDebouncer} from './state-debouncer.provider';
export class FiltersProvider {
/**
 * @param {?} _page
 * @param {?} stateDebouncer
 */
constructor(private _page: Page,
private stateDebouncer: StateDebouncer) {}
/**
 * This subject is the list of filters that changed last, not the whole list.
 * We emit a list rather than just one filter to allow batch changes to several at once.
 */
private _change = new Subject<Filter<any>[]>();
/**
 * @return {?}
 */
public get change(): Observable<Filter<any>[]> {
        return this._change.asObservable();
    }
/**
 * List of all filters, whether they're active or not
 */
private _all: RegisteredFilter<any>[] = [];
/**
 * Tests if at least one filter is currently active
 * @return {?}
 */
public hasActiveFilters(): boolean {
        // We do not use getActiveFilters() because this function will be called much more often
        // and stopping the loop early might be relevant.
        for (const {filter} of this._all) {
            if (filter && filter.isActive()) {
                return true;
            }
        }
        return false;
    }
/**
 * Returns a list of all currently active filters
 * @return {?}
 */
public getActiveFilters(): Filter<any>[] {
        const /** @type {?} */ ret: Filter<any>[] = [];
        for (const {filter} of this._all) {
            if (filter && filter.isActive()) {
                ret.push(filter);
            }
        }
        return ret;
    }
/**
 * Registers a filter, and returns a deregistration function
 * @template F
 * @param {?} filter
 * @return {?}
 */
public add<F extends Filter<any>>(filter: F): RegisteredFilter<F> {
        const /** @type {?} */ index = this._all.length;
        const /** @type {?} */ subscription = filter.changes.subscribe(() => this.resetPageAndEmitFilterChange([filter]));
        let /** @type {?} */ hasUnregistered = false;
        const /** @type {?} */ registered = new RegisteredFilter(filter, () => {
            if (hasUnregistered) {
                return;
            }
            subscription.unsubscribe();
            this._all.splice(index, 1);
            if (filter.isActive()) {
                this.resetPageAndEmitFilterChange([]);
            }
            hasUnregistered = true;
        });
        this._all.push(registered);
        if (filter.isActive()) {
            this.resetPageAndEmitFilterChange([filter]);
        }
        return registered;
    }
/**
 * Accepts an item if it is accepted by all currently active filters
 * @param {?} item
 * @return {?}
 */
public accepts(item: any): boolean {
        for (const {filter} of this._all) {
            if (filter && filter.isActive() && !filter.accepts(item)) {
                return false;
            }
        }
        return true;
    }
/**
 * @param {?} filters
 * @return {?}
 */
private resetPageAndEmitFilterChange(filters: Filter<any>[]) {
        this.stateDebouncer.changeStart();
        // filtering may change the page number such that current page number doesn't exist in the filtered dataset.
        // So here we always set the current page to 1 so that it'll fetch first page's data with the given filter.
        this._page.current = 1;
        this._change.next(filters);
        this.stateDebouncer.changeDone();
    }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Page, },
{type: StateDebouncer, },
];
}

function FiltersProvider_tsickle_Closure_declarations() {
/** @type {?} */
FiltersProvider.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FiltersProvider.ctorParameters;
/**
 * This subject is the list of filters that changed last, not the whole list.
 * We emit a list rather than just one filter to allow batch changes to several at once.
 * @type {?}
 */
FiltersProvider.prototype._change;
/**
 * List of all filters, whether they're active or not
 * @type {?}
 */
FiltersProvider.prototype._all;
/** @type {?} */
FiltersProvider.prototype._page;
/** @type {?} */
FiltersProvider.prototype.stateDebouncer;
}

export class RegisteredFilter<F extends Filter<any>> {
/**
 * @param {?} filter
 * @param {?} unregister
 */
constructor(public filter: F,
public unregister: () => void) {}
}

function RegisteredFilter_tsickle_Closure_declarations() {
/** @type {?} */
RegisteredFilter.prototype.filter;
/** @type {?} */
RegisteredFilter.prototype.unregister;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
